<script setup lang="ts">
import { inject, onBeforeMount, ref, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import omit from 'lodash/omit';
import { TOTAL_COST } from '@/constants';
import { useComponentComposable } from '@/components/useComponentComposable';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useWorksheetComposable } from './useWorksheetComposable';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';

const dialogRef = inject('dialogRef') as Ref;
const hotelData = dialogRef?.value?.data?.hotelData;
const roomData = dialogRef?.value?.data?.roomData;
const flightData = dialogRef?.value?.data?.flightData;
const oldRoomsValue = dialogRef?.value?.data?.oldRoomsValue;
const onSaveSelectRoom = dialogRef?.value?.options?.emits?.onSaveSelectRoom;
const promotions = dialogRef?.value?.data?.promotions;
const additionalHotelDetail = dialogRef?.value?.data?.additionalHotelDetail;
const permissions = dialogRef?.value?.data?.permissions;

const expandedDetail = ref([]);
const listRoomValue = ref([]);
const isLimitSelect = ref(false);
const selectedItems = ref([]);
const selectedRooms = ref([]);
const unselectedRoomsGreaterZero = ref([]);
const unselectedRoomsZero = ref([]);
const isSortByName = ref(false);
const isSortBySinglePrice = ref(false);
const isSortByDoublePrice = ref(false);

const currentLanguage = getCurrentLocale();
const { renderHTMLContent } = useComponentComposable();
const { filterPriceList, PRICE_KEY, titleHotel, getCabinName } = useWorksheetComposable();

const convertOccNameTable = (rowName: string) => {
  return titleHotel.find((th) => th.key === rowName)?.name;
};

const expandPricingFunc = (index) => {
  expandedDetail.value[index].isExpended = !expandedDetail.value[index].isExpended;
};

const handleCloseAndApply = (selectedRoom) => {
  hotelData.rooms = listRoomValue.value?.filter((room, index) => selectedRoom[index].isSelected);
  onSaveSelectRoom(hotelData);
};

const selectRoom = (expandedDetailList: any[]) => {
  const selectedItem = expandedDetailList.filter((item) => item?.isSelected) || [];
  hotelData.rooms = listRoomValue.value?.filter((room, index) => expandedDetailList[index].isSelected);
  selectedItems.value = selectedItem;
  const validateSelectRoom = oldRoomsValue?.length > 3 ? oldRoomsValue?.length : 3;
  if (selectedItem.length >= validateSelectRoom) {
    isLimitSelect.value = true;
  } else {
    isLimitSelect.value = false;
  }
};

const getTotalPricing = (priceValue) => {
  const cloneData = omit(priceValue, ['childPrices']);
  return Object.values(cloneData).reduce((acc, value) => acc + parseFloat(value), 0);
};

const getListRoomSelected = (listRoom) => {
  return listRoom?.filter((roomData) => hotelData.rooms.some((roomSelected) => roomSelected.code === roomData.code));
};

const getOtherZero = (listRoom) => {
  const listRoomData = listRoom?.filter((roomData) => {
    return getTotalPricing(roomData.price.sellingPrice) === 0;
  });
  return listRoomData.filter((roomData) => {
    return !getListRoomSelected(listRoom).some((roomSelected) => roomSelected.code === roomData.code);
  });
};

const getOtherGreaterZero = (listRoom) => {
  const listRoomData = listRoom?.filter((roomData) => {
    return getTotalPricing(roomData.price.sellingPrice) > 0;
  });
  if (!listRoomData || listRoomData.length === 0) {
    return [];
  }
  return listRoomData.filter((roomData) => {
    return !getListRoomSelected(listRoom).some((roomSelected) => roomSelected.code === roomData.code);
  });
};

const initData = (isInit, roomValue) => {
  selectedRooms.value = getListRoomSelected(roomValue);
  unselectedRoomsGreaterZero.value = getOtherGreaterZero(roomValue);
  unselectedRoomsZero.value = getOtherZero(roomValue);
  listRoomValue.value = [...selectedRooms.value, ...unselectedRoomsGreaterZero.value, ...unselectedRoomsZero.value];
  renderExpendedDetail(isInit);
};

const renderExpendedDetail = (init) => {
  expandedDetail.value = listRoomValue.value?.map((room, index) => {
    const isSelect = hotelData.rooms.some((roomSelected) => roomSelected.code === room.code);
    return { ...room, isSelected: isSelect, isExpended: init ? index === 0 : expandedDetail.value[index]?.isExpended };
  });

  selectedItems.value = expandedDetail.value?.filter((item) => item?.isSelected) || [];
};

const sortByName = (roomValue, lang) => {
  return roomValue?.sort((roomFirst, roomSecond) => {
    const longNameFirst = getTextVariant(lang, roomFirst?.longName, roomFirst?.longNameFr);
    const longNameSecond = getTextVariant(lang, roomSecond?.longName, roomSecond?.longNameFr);
    const nameFirst = longNameFirst.toLowerCase();
    const nameSecond = longNameSecond.toLowerCase();
    return nameFirst.localeCompare(nameSecond);
  });
};

const sortByPrice = (roomValue, type) => {
  return roomValue?.sort((roomFirst, roomSecond) => {
    const priceFirst = parseFloat(roomFirst.price.sellingPrice[type]);
    const priceSecond = parseFloat(roomSecond.price.sellingPrice[type]);

    if (priceFirst === 0) {
      return 1;
    } else if (priceSecond === 0) {
      return -1;
    }

    return priceFirst - priceSecond;
  });
};

const handleSortByName = (data) => {
  isSortBySinglePrice.value = false;
  isSortByDoublePrice.value = false;
  isSortByName.value = !data;
  initData(false, roomData);
  if (isSortByName.value) {
    listRoomValue.value = [...sortByName(selectedRooms.value, currentLanguage), ...sortByName(unselectedRoomsGreaterZero.value, currentLanguage), ...sortByName(unselectedRoomsZero.value, currentLanguage)];
    renderExpendedDetail();
  }
};

const handleSortBySinglePrice = (data) => {
  isSortBySinglePrice.value = !data;
  isSortByDoublePrice.value = false;
  isSortByName.value = false;
  initData(false, roomData);
  if (isSortBySinglePrice.value) {
    listRoomValue.value = [...sortByPrice(selectedRooms.value, 'singlePrice'), ...sortByPrice(unselectedRoomsGreaterZero.value, 'singlePrice'), ...unselectedRoomsZero.value];
    renderExpendedDetail();
  }
};

const handleSortByDoublePrice = (data) => {
  isSortByDoublePrice.value = !data;
  isSortBySinglePrice.value = false;
  isSortByName.value = false;
  initData(false, roomData);
  if (isSortByDoublePrice.value) {
    listRoomValue.value = [...sortByPrice(selectedRooms.value, 'doublePrice'), ...sortByPrice(unselectedRoomsGreaterZero.value, 'doublePrice'), ...unselectedRoomsZero.value];
    renderExpendedDetail();
  }
};

const getRoomText = (shortText: string, longText?: string): string => {
  return longText ? shortText + ' - ' + longText : shortText;
};

const handleDisplayName = (room: any, lang: string) => {
  const textFr = getRoomText(room?.roomFr, room?.longNameFr);
  const textEn = getRoomText(room?.room, room?.longName);
  return `${getTextVariant(lang, textEn, textFr)} - ${room.code}`;
};

onBeforeMount(() => {
  if (hotelData) {
    initData(true, roomData);
    selectRoom(expandedDetail.value);
  }
});
</script>

<template>
  <div class="mt-4">
    <div class="pricing mt-2" v-if="flightData?.length > 0">
      <div class="lead-option">{{ $t('common.flight') }}</div>
      <div class="detail">
        <DataTable :value="flightData" class="w-full" showGridlines>
          <TableColumn field="type" :header="$t('worksheet.type')" dataType="string">
            <template #body="{ data }">
              {{ $t(`worksheet.${data.type.toLowerCase()}`) }}
            </template>
          </TableColumn>
          <TableColumn field="operated" :header="$t('worksheet.airline')" dataType="string"> </TableColumn>
          <TableColumn field="flightNumber" :header="$t('worksheet.flight_number')" dataType="string"> </TableColumn>
          <TableColumn field="gate" :header="$t('worksheet.from')" dataType="string">
            <template #body="{ data }">
              {{ getTextVariant(currentLanguage, data.gate, data.gateFr) }}
            </template>
          </TableColumn>
          <TableColumn field="dest" :header="$t('worksheet.to')" dataType="string">
            <template #body="{ data }">
              {{ getTextVariant(currentLanguage, data.dest, data.destFr) }}
            </template>
          </TableColumn>
          <TableColumn field="depDate" :header="$t('worksheet.dep_date')" dataType="date">
            <template #body="{ data }">
              {{ formatDate(data.depDate, 'dd-LLL-yyyy', true, currentLanguage) }}
            </template>
          </TableColumn>
          <TableColumn field="depTime" :header="$t('worksheet.dep_time')" dataType="date">
            <template #body="{ data }">
              {{ data.depTime }}
            </template>
          </TableColumn>
          <TableColumn field="arrDate" :header="$t('worksheet.arrival_date')">
            <template #body="{ data }">
              {{ formatDate(data.arrDate, 'dd-LLL-yyyy', true, currentLanguage) }}
            </template>
          </TableColumn>
          <TableColumn field="arrTime" :header="$t('worksheet.arrival_time')" dataType="date">
            <template #body="{ data }">
              {{ data.arrTime }}
            </template>
          </TableColumn>
          <TableColumn field="via" :header="$t('worksheet.via')" dataType="date"> </TableColumn>
          <TableColumn field="fClass" :header="$t('common.class')" dataType="date"> </TableColumn>
          <TableColumn field="cabin" :header="$t('common.cabin')" dataType="string" class="word-break">
            <template #body="{ data, field }">
              <span>{{ getCabinName(data[field]) }}</span>
            </template>
          </TableColumn>
        </DataTable>
      </div>
    </div>
    <div class="mt-5" v-if="hotelData?.hotel">
      <div class="lead-option">{{ `${hotelData?.hotel} ${$t('common.hotel').toUpperCase()} (${hotelData?.hCode})` }}</div>
      <div class="mt-2 w-full flex gap-2 justify-content-end">
        <Button severity="secondary" :class="[`surface-border border-2 ${isSortByName ? 'surface-200' : 'surface-card '}`]" text :label="`${$t('common.buttons.sort_name')} (A-Z)`" @click="handleSortByName(isSortByName)" />
        <Button
          severity="secondary"
          :class="[`surface-border border-2 ${isSortBySinglePrice ? 'surface-200' : 'surface-card '}`]"
          text
          :label="`${$t('common.buttons.sort_single')} (${$t('common.buttons.low_high')})`"
          @click="handleSortBySinglePrice(isSortBySinglePrice)"
        />
        <Button
          severity="secondary"
          :class="[`surface-border border-2 ${isSortByDoublePrice ? 'surface-200' : 'surface-card '}`]"
          text
          :label="`${$t('common.buttons.sort_double')} (${$t('common.buttons.low_high')})`"
          @click="handleSortByDoublePrice(isSortByDoublePrice)"
        />
      </div>
      <div v-for="(room, indexHot) in listRoomValue" :key="indexHot">
        <div class="pricing mt-3">
          <div class="lead-pricing flex align-items-center justify-content-between">
            <div class="flex gap-2">
              <div>{{ $t('common.pricing') }} ({{ handleDisplayName(room, currentLanguage) }})</div>
              <div class="flex align-items-center cursor-pointer" @click="expandPricingFunc(indexHot)">[&nbsp; <i class="pi pi-minus" v-if="expandedDetail[indexHot].isExpended"></i> <i class="pi pi-plus" v-else></i>&nbsp;]</div>
            </div>
            <div><Checkbox :disabled="!expandedDetail[indexHot].isSelected && isLimitSelect" v-model="expandedDetail[indexHot].isSelected" @change="selectRoom(expandedDetail)" :binary="true" /></div>
          </div>
          <div class="pricing-detail" v-show="expandedDetail[indexHot].isExpended">
            <DataTable :value="filterPriceList(room.price, permissions)" class="w-full" data-key="rowName" showGridlines>
              <TableColumn header="" dataType="string" class="relative">
                <template #body="{ data }">
                  <div :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'text-900 font-bold']">
                    <span>{{ convertOccNameTable(data.rowName) }}</span>
                  </div>
                  <div v-if="data.key === TOTAL_COST" class="opacity-0">
                    <span>{{ convertOccNameTable(data.rowName) }}</span>
                  </div>
                </template>
              </TableColumn>
              <TableColumn field="singlePrice" :header="$t('book_form.single')" dataType="number" class="relative">
                <template #body="{ data, field, index }">
                  <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
              <TableColumn field="doublePrice" :header="$t('book_form.double')" dataType="number" class="relative">
                <template #body="{ data, field, index }">
                  <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
              <TableColumn field="triplePrice" :header="$t('book_form.triple')" dataType="number" class="relative">
                <template #body="{ data, field, index }">
                  <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
              <TableColumn field="quadPrice" :header="$t('book_form.quad')" dataType="number" class="relative">
                <template #body="{ data, field, index }">
                  <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
              <TableColumn field="fiveInRoomPrice" :header="$t('book_form.5_in_room')" dataType="number" class="relative">
                <template #body="{ data, field, index }">
                  <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
              <TableColumn field="sixInRoomPrice" :header="$t('book_form.6_in_room')" dataType="number" class="relative">
                <template #body="{ data, field, index }">
                  <div v-if="index" :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
              <TableColumn field="childOnePrice" :header="$t('common.child')" class="min-w-8rem relative">
                <template #body="{ data, field, index }">
                  <div v-if="index === 0">
                    {{ data[field] }}
                  </div>
                  <div v-else :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header ']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                  <InputText v-else inputId="locale-us" locale="en-US" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
              <TableColumn field="childTwoPrice" :header="$t('common.child')" class="min-w-8rem relative">
                <template #body="{ data, field, index }">
                  <div v-if="index === 0">
                    {{ data[field] }}
                  </div>
                  <div v-else :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                  <InputText v-else inputId="locale-us" locale="en-US" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
              <TableColumn field="childThreePrice" :header="$t('common.child')" class="min-w-8rem relative">
                <template #body="{ data, field, index }">
                  <div v-if="index === 0">
                    {{ data[field] }}
                  </div>
                  <div v-else :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                  <InputText v-else inputId="locale-us" locale="en-US" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
              <TableColumn field="infantPrice" :header="$t('common.infant')" dataType="number" class="relative">
                <template #body="{ data, field, index }">
                  <div v-if="index === 0">
                    {{ data[field] }}
                  </div>
                  <div v-else :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium', data.key === PRICE_KEY.HEADER && 'bg-white price-header']">
                    <span :class="data.key === PRICE_KEY.HEADER && 'opacity-0'">{{ data[field] ? Number(data[field]).toFixed(2) || data[field] : '0.00' }}</span>
                  </div>
                </template>
                <template #editor="{ data, field, index }">
                  <InputNumber v-if="index !== 0" inputId="locale-us" locale="en-US" :minFractionDigits="2" v-model="data[field]" class="w-full" />
                  <InputText v-else inputId="locale-us" locale="en-US" v-model="data[field]" class="w-full" />
                </template>
              </TableColumn>
            </DataTable>
          </div>
        </div>
      </div>
      <div class="text-center mt-2 w-full">
        <Button icon="pi pi-check" :disabled="!selectedItems.length" :label="$t('common.buttons.close_apply')" @click="handleCloseAndApply(expandedDetail)" />
      </div>
    </div>
    <div class="promotions" v-if="promotions">
      <div class="lead-promotions my-3">{{ $t('worksheet.promotions') }}</div>
      <div class="flex justify-content-between gap-3">
        <renderHTMLContent :data="promotions" htmlElement="p"></renderHTMLContent>
      </div>
    </div>
    <div class="additional-hotel mt-5" v-if="additionalHotelDetail">
      <div class="lead-additional my-3">{{ $t('hotel_contract.additional_hotel_detail') }}</div>
      <div class="flex justify-content-between gap-3">
        <renderHTMLContent :data="additionalHotelDetail" htmlElement="p"></renderHTMLContent>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .background-opacity {
    background: rgba($color: #ddd, $alpha: 0.5);
  }

  .min-w-8rem {
    min-width: 8rem;
  }
}
.lead-option {
  background-color: $col-primary;
  color: white;
  padding: 0.8rem 1.2rem;
  font-weight: 700;
}

.lead-promotions,
.lead-additional,
.lead-pricing {
  background-color: rgb(115, 115, 115);
  color: white;
  padding: 0.8rem 1.2rem;
  font-weight: 700;
}

.pricing {
  table {
    border-collapse: collapse;
    width: 100%;
  }

  td,
  th {
    border: 1px solid black;
    text-align: left;
    padding: 8px;
    font-weight: 400;
  }

  tr:nth-child(even) {
    background-color: #dddddd;
  }

  tr:first-child th {
    font-weight: 700;
  }

  tr td:first-child {
    font-weight: 700;
  }

  tr:last-child {
    background-color: black;
    color: white;

    td {
      border: 1px solid white;
    }
  }
}
</style>
